import { useEffect } from "react";
import { confirmFormSubmission } from "./submissions.functions";
import type { FunnelStep } from "./forms";

/**
 * When a visitor reaches the screen that only appears after submitting a form,
 * ask the server to confirm against Agentmoves that a lead really arrived.
 * Retries a few times (GHL takes a few seconds to create the lead) and only
 * marks the step done once the server actually confirms.
 */
function sessionId(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return window.sessionStorage.getItem("kcn_analytics_sid") ?? undefined;
}

const inFlight = new Set<string>();

const RETRY_DELAYS_MS = [0, 5000, 15000, 30000, 60000];

export function confirmSubmission(step: FunnelStep, path: string) {
  if (typeof window === "undefined") return;
  const key = `kcn_confirm_${step}`;
  if (inFlight.has(key) || window.sessionStorage.getItem(key) === "done") return;
  inFlight.add(key);

  const attempt = async (i: number): Promise<void> => {
    if (i >= RETRY_DELAYS_MS.length) {
      inFlight.delete(key);
      return;
    }
    const delay = RETRY_DELAYS_MS[i] ?? 0;
    if (delay) await new Promise((r) => window.setTimeout(r, delay));
    try {
      const res = await confirmFormSubmission({ data: { step, path, sessionId: sessionId() } });
      if (res?.confirmed) {
        window.sessionStorage.setItem(key, "done");
        inFlight.delete(key);
        return;
      }
      if (res?.reason === "not_configured") {
        inFlight.delete(key);
        return;
      }
    } catch {
      // network hiccup: fall through to retry
    }
    await attempt(i + 1);
  };

  void attempt(0);
}

/** True when a postMessage from the Agentmoves embed looks like a submit. */
function looksLikeSubmit(data: unknown): boolean {
  const text =
    typeof data === "string" ? data : data && typeof data === "object" ? JSON.stringify(data) : "";
  return /form[-_ ]?submit|submitted|onFormSubmit|thank[-_ ]?you/i.test(text);
}

/**
 * Listens for the Agentmoves form embed reporting a successful submit, then
 * confirms it. Safe to mount on any page that hosts one of the form iframes.
 */
export function useGhlSubmitConfirm(step: FunnelStep, path: string) {
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      let host = "";
      try {
        host = new URL(e.origin).hostname;
      } catch {
        return;
      }
      if (!/(agent-moves\.com|leadconnectorhq\.com|msgsndr\.com)$/i.test(host)) return;
      if (looksLikeSubmit(e.data)) confirmSubmission(step, path);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [step, path]);
}
